import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Select,
  MenuItem,
  Button
} from '@mui/material';
import FormField from './FormField';
import ApiSelect from './ApiSelect';
import { MONTHS, extractName, standardInputSx } from '../utils/hubsHelpers';

const RetreatFiltersCard = ({
  selectedFilters,
  onFilterChange,
  atmosphereDropdown,
  featuresDropdown,
  surroundingAreasDropdown,
  thingsInAreaDropdown,
  mealPlanDropdown,
  languageDropdown,
  matchingRetreats = [],
  sortOnPage,
  setSortOnPage,
  retreatsList = [],
  openDrawer
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: '8px',
        p: '14px 16px',
        mb: 1.75,
        boxShadow: 'none'
      }}
    >
      <Typography variant="subtitle1" sx={{ fontSize: 13.5, fontWeight: 600, color: '#1E293B', mb: 0.35, display: 'flex', alignItems: 'center' }}>
        Retreat Filters{' '}
        <Box component="span" sx={{ backgroundColor: '#803927', color: '#fff', fontSize: 9.5, px: '5px', py: '1px', borderRadius: '4px', fontWeight: 600, ml: 0.75 }}>
          NEW
        </Box>
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748B', fontSize: 11, mb: 1.25, lineHeight: 1.4 }}>
        Which retreats appear on this page. All filter dropdowns load options directly from backend APIs upon clicking. Change one and matching counts update instantly.
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.25, mb: 1.25 }}>
        {/* 1. Dates */}
        <FormField label="Dates">
          <Select
            size="small"
            fullWidth
            value={extractName(selectedFilters.dates) || ''}
            onChange={(e) => onFilterChange('dates', e.target.value)}
            displayEmpty
            renderValue={(s) => extractName(s) || 'Any'}
            sx={standardInputSx}
          >
            <MenuItem value="">Any</MenuItem>
            {['One time', 'Multiple dates', 'All year round'].map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormField>

        {/* 2. Month / Season */}
        <FormField label="Month / Season">
          <Select
            size="small"
            fullWidth
            value={extractName(selectedFilters.month) || ''}
            onChange={(e) => onFilterChange('month', e.target.value)}
            displayEmpty
            renderValue={(s) => extractName(s) || 'Any'}
            sx={standardInputSx}
          >
            <MenuItem value="">Any</MenuItem>
            {[
              ...MONTHS,
              'Summer (Apr to Jun)',
              'Monsoon (Jun to Sep)',
              'Winter (Oct to Mar)'
            ].map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormField>

        {/* 3. Duration */}
        <FormField label="Duration">
          <Select
            size="small"
            fullWidth
            value={extractName(selectedFilters.dur) || ''}
            onChange={(e) => onFilterChange('dur', e.target.value)}
            displayEmpty
            renderValue={(s) => extractName(s) || 'Any'}
            sx={standardInputSx}
          >
            <MenuItem value="">Any</MenuItem>
            {['2 to 3 days', '4 to 5 days', '6 to 7 days', '8 to 14 days', '15 days or more'].map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormField>

        {/* 4. Price Band */}
        <FormField label="Price band">
          <Select
            size="small"
            fullWidth
            value={extractName(selectedFilters.price) || ''}
            onChange={(e) => onFilterChange('price', e.target.value)}
            displayEmpty
            renderValue={(s) => extractName(s) || 'Any'}
            sx={standardInputSx}
          >
            <MenuItem value="">Any</MenuItem>
            {[
              'Under INR 10,000',
              'INR 10,000 to 25,000',
              'INR 25,000 to 75,000',
              'INR 75,000 to 150,000',
              'Above INR 150,000'
            ].map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormField>

        {/* 5. Atmosphere (API-driven) */}
        <FormField label="Atmosphere">
          <ApiSelect
            value={selectedFilters.atm || ''}
            onChange={(e) => onFilterChange('atm', e.target.value, atmosphereDropdown.options, 'atmosphereId')}
            dropdown={atmosphereDropdown}
            placeholder="Any"
            emptyOptionLabel="Any"
          />
        </FormField>

        {/* 6. Features (API-driven) */}
        <FormField label="Features">
          <ApiSelect
            value={selectedFilters.feat || ''}
            onChange={(e) => onFilterChange('feat', e.target.value, featuresDropdown.options, 'featuresId')}
            dropdown={featuresDropdown}
            placeholder="Any"
            emptyOptionLabel="Any"
          />
        </FormField>

        {/* 7. Surrounding Areas (API-driven) */}
        <FormField label="Surrounding areas">
          <ApiSelect
            value={selectedFilters.sur || ''}
            onChange={(e) => onFilterChange('sur', e.target.value, surroundingAreasDropdown.options, 'surroundingAreasId')}
            dropdown={surroundingAreasDropdown}
            placeholder="Any"
            emptyOptionLabel="Any"
          />
        </FormField>

        {/* 8. Things to do nearby (API-driven) */}
        <FormField label="Things to do nearby">
          <ApiSelect
            value={selectedFilters.near || ''}
            onChange={(e) => onFilterChange('near', e.target.value, thingsInAreaDropdown.options, 'thingsInAreaId')}
            dropdown={thingsInAreaDropdown}
            placeholder="Any"
            emptyOptionLabel="Any"
          />
        </FormField>

        {/* 9. Food / Meal Plan (API-driven) */}
        <FormField label="Food">
          <ApiSelect
            value={selectedFilters.food || ''}
            onChange={(e) => onFilterChange('food', e.target.value, mealPlanDropdown.options, 'mealPlanId')}
            dropdown={mealPlanDropdown}
            placeholder="Any"
            emptyOptionLabel="Any"
          />
        </FormField>

        {/* 10. Language (API-driven) */}
        <FormField label="Language">
          <ApiSelect
            value={selectedFilters.lang || ''}
            onChange={(e) => onFilterChange('lang', e.target.value, languageDropdown.options, 'languageId')}
            dropdown={languageDropdown}
            placeholder="Any"
            emptyOptionLabel="Any"
          />
        </FormField>

        {/* 11. Skill Level */}
        <FormField label="Skill level">
          <Select
            size="small"
            fullWidth
            value={extractName(selectedFilters.skill) || ''}
            onChange={(e) => onFilterChange('skill', e.target.value)}
            displayEmpty
            renderValue={(s) => extractName(s) || 'Any'}
            sx={standardInputSx}
          >
            <MenuItem value="">Any</MenuItem>
            {['Beginner', 'Intermediate', 'Advanced'].map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormField>

        {/* 12. Age Group */}
        <FormField label="Age group">
          <Select
            size="small"
            fullWidth
            value={extractName(selectedFilters.age) || ''}
            onChange={(e) => onFilterChange('age', e.target.value)}
            displayEmpty
            renderValue={(s) => extractName(s) || 'Any'}
            sx={standardInputSx}
          >
            <MenuItem value="">Any</MenuItem>
            {['All Ages', '18 - 24', '25 - 40', '41 - 60', '60+'].map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormField>
      </Box>

      {/* Match Banner with Sort on page */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: matchingRetreats.length === 0 ? '#FEF2F2' : '#F0FDF4',
          border: `1px solid ${matchingRetreats.length === 0 ? '#FECACA' : '#BBF7D0'}`,
          borderRadius: '6px',
          p: '8px 12px',
          gap: 1.25,
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography component="span" sx={{ fontWeight: 600, color: matchingRetreats.length === 0 ? '#DC2626' : '#2E7D32', fontSize: 12 }}>
            {matchingRetreats.length} retreat{matchingRetreats.length === 1 ? '' : 's'}
          </Typography>
          <Typography component="span" sx={{ color: '#1E293B', fontSize: 12 }}>
            {matchingRetreats.length > 0 ? 'match these filters right now.' : 'match. Loosen a filter.'}
          </Typography>
          <Button
            variant="text"
            size="small"
            disableRipple
            onClick={() => openDrawer('list')}
            sx={{
              color: '#803927',
              p: 0,
              minWidth: 'auto',
              fontWeight: 600,
              fontSize: 12,
              textDecoration: 'underline',
              textTransform: 'none',
              '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
            }}
          >
            View list
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#64748B', fontSize: 12 }}>
          <span>Sort on page:</span>
          <Select
            size="small"
            value={sortOnPage}
            onChange={(e) => setSortOnPage(e.target.value)}
            sx={{
              fontSize: 12,
              height: 32,
              backgroundColor: '#FFFFFF',
              borderRadius: '6px',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFBD65' }
            }}
          >
            <MenuItem value="Platform score">Platform score</MenuItem>
            <MenuItem value="Price, low to high">Price, low to high</MenuItem>
            <MenuItem value="Newest first">Newest first</MenuItem>
          </Select>
        </Box>
      </Box>
    </Paper>
  );
};

export default React.memo(RetreatFiltersCard);
